<template>
  <div class="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 space-y-3">
    <div class="flex items-center justify-between">
      <h3 class="text-sm font-semibold text-gray-900 dark:text-white">
        <UiSpinner v-if="status === 'starting' || status === 'running'"
          :size="4" screen-reader="Scanning" class="inline-block mr-2 align-middle" />
        Competitor scan in progress
      </h3>
      <span class="text-xs text-gray-500">{{ statusLabel }}</span>
    </div>

    <div v-if="total > 0" class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2"
      v-tooltip="`${completed} of ${total} queries complete`">
      <div class="bg-primary-500 h-2 rounded-full transition-all duration-200"
        :style="{ width: `${progressPercent}%` }" />
    </div>

    <p class="text-xs text-gray-500">
      <span v-if="latestQuery">Searching: <code class="text-gray-700">{{ latestQuery }}</code></span>
      <span v-if="partialCount > 0"> · {{ partialCount }} results so far</span>
    </p>

    <p v-if="error" class="text-sm text-red-600">
      {{ error }}
    </p>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import vTooltip from '~/javascript/directives/tooltip'
import UiSpinner from '~/javascript/components/ui/spinner.vue'
import { toastSuccess, toastError } from '~/javascript/utils/toast'
import { subscribeToScanRun } from '~/javascript/channels/negative_keyword_scan_channel'

const props = defineProps({
  scanRunId: { type: String, required: true }
})

const emit = defineEmits(['completed', 'failed'])

const completed = ref(0)
const total = ref(0)
const latestQuery = ref('')
const partialCount = ref(0)
const status = ref('starting')
const error = ref(null)

let subscription = null

const progressPercent = computed(() => (total.value > 0 ? Math.round((completed.value / total.value) * 100) : 0))

const statusLabel = computed(() => {
  switch (status.value) {
    case 'starting': return 'Starting…'
    case 'running': return `${completed.value}/${total.value} queries`
    case 'completed': return 'Done!'
    case 'failed': return 'Failed'
    default: return status.value
  }
})

const onMessage = (data) => {
  switch (data.event) {
    case 'started':
      status.value = 'running'
      break
    case 'progress':
      completed.value = data.completed
      total.value = data.total
      latestQuery.value = data.latest_query
      partialCount.value = data.partial_count
      break
    case 'completed':
      status.value = 'completed'
      toastSuccess(`Scan complete — ${partialCount.value} result${partialCount.value === 1 ? '' : 's'} ready to review.`)
      emit('completed')
      break
    case 'failed':
      status.value = 'failed'
      error.value = data.message
      toastError(new Error(data.message || 'Scan failed'))
      emit('failed', data.message)
      break
    default:
      break
  }
}

onMounted(() => {
  subscription = subscribeToScanRun(props.scanRunId, { onMessage })
})

onBeforeUnmount(() => {
  subscription?.unsubscribe()
})
</script>
